import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

function PDFPreview({ url }) {
  return (
    <div className="w-full h-64 bg-slate-100 dark:bg-slate-700 rounded-t-lg overflow-hidden">
      <iframe
        src={`${url}#view=FitH&embedded=true&toolbar=0&navpanes=0&scrollbar=0&pagemode=none`}
        title="PDF Preview"
        width="100%"
        height="100%"
        frameBorder="0"
        className="w-full h-full"
      />
    </div>
  );
}

function Downloads() {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language;
  const [fileInfo, setFileInfo] = useState({});
  const [loading, setLoading] = useState(true);

  const getDocLangFlag = (lang) => {
    switch (lang) {
      case "zh":
        return "/images/flags/CN.png";
      case "en":
        return "/images/flags/GB.png";
      case "de":
        return "/images/flags/DE.png";
      default:
        return "/images/flags/GB.png";
    }
  };

  const fetchFileInfo = async (url) => {
    try {
      const response = await fetch(url, { method: "HEAD" });
      
      if (!response.ok) {
        console.warn(`Datei nicht gefunden oder Fehler: ${url} - Status: ${response.status}`);
        // Versuche mit GET-Request, um Größe zu ermitteln
        return await fetchFileInfoWithGet(url);
      }

      // Last-Modified Header
      const lastModifiedHeader = response.headers.get("last-modified");
      let date = "Unbekannt";
      if (lastModifiedHeader) {
        try {
          const lastModified = new Date(lastModifiedHeader);
          if (!isNaN(lastModified.getTime())) {
            date = lastModified.toLocaleDateString("de-DE", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric"
            });
          }
        } catch (dateError) {
          console.warn("Fehler beim Parsen des Datums:", dateError);
        }
      }

      // Content-Length Header
      const contentLengthHeader = response.headers.get("content-length");
      let size = "0 MB";
      if (contentLengthHeader) {
        const contentLength = parseInt(contentLengthHeader, 10);
        if (!isNaN(contentLength) && contentLength > 0) {
          size = formatFileSize(contentLength);
        }
      }

      // Wenn keine Größe gefunden, versuche GET-Request
      if (size === "0 MB") {
        const getInfo = await fetchFileInfoWithGet(url);
        if (getInfo.size !== "0 MB") {
          size = getInfo.size;
        }
        if (date === "Unbekannt" && getInfo.date !== "Unbekannt") {
          date = getInfo.date;
        }
      }

      return {
        date,
        size,
      };
    } catch (error) {
      console.error("Fehler beim Abrufen der Dateiinformationen:", error, url);
      // Versuche mit GET-Request als Fallback
      try {
        return await fetchFileInfoWithGet(url);
      } catch (getError) {
        console.error("Auch GET-Request fehlgeschlagen:", getError);
        return {
          date: "Unbekannt",
          size: "Unbekannt",
        };
      }
    }
  };

  const fetchFileInfoWithGet = async (url) => {
    try {
      const response = await fetch(url, { method: "GET" });
      
      if (!response.ok) {
        return {
          date: "Unbekannt",
          size: "Unbekannt",
        };
      }

      // Last-Modified Header
      const lastModifiedHeader = response.headers.get("last-modified");
      let date = "Unbekannt";
      if (lastModifiedHeader) {
        try {
          const lastModified = new Date(lastModifiedHeader);
          if (!isNaN(lastModified.getTime())) {
            date = lastModified.toLocaleDateString("de-DE", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric"
            });
          }
        } catch (dateError) {
          console.warn("Fehler beim Parsen des Datums:", dateError);
        }
      }

      // Content-Length Header
      const contentLengthHeader = response.headers.get("content-length");
      let size = "Unbekannt";
      if (contentLengthHeader) {
        const contentLength = parseInt(contentLengthHeader, 10);
        if (!isNaN(contentLength) && contentLength > 0) {
          size = formatFileSize(contentLength);
        }
      }

      // Wenn immer noch keine Größe, versuche aus Blob zu ermitteln
      if (size === "Unbekannt") {
        const blob = await response.blob();
        if (blob && blob.size > 0) {
          size = formatFileSize(blob.size);
        }
      }

      return {
        date,
        size,
      };
    } catch (error) {
      console.error("Fehler beim GET-Request:", error);
      return {
        date: "Unbekannt",
        size: "Unbekannt",
      };
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes || isNaN(bytes) || bytes === 0) return "0 MB";
    const bytesNum = typeof bytes === 'string' ? parseInt(bytes, 10) : bytes;
    if (isNaN(bytesNum) || bytesNum === 0) return "0 MB";
    
    const mb = bytesNum / (1024 * 1024);
    if (mb < 0.1) {
      const kb = bytesNum / 1024;
      return `${kb.toFixed(1)} KB`;
    }
    return `${mb.toFixed(1)} MB`;
  };

  const downloadItems = useMemo(() => [
    {
      icon: "fas fa-file-pdf",
      title: t("downloads.tenderTitle"),
      description: t("downloads.tenderDescription"),
      downloadUrl:
        currentLang === "zh"
          ? "/documents/第十八届世界精武武术锦标赛暨文化大会.pdf"
          : "/documents/18th_World_Chin_Woo_Martial_Arts_Championship_&_Culture_Assembly.pdf",
      fileName:
        currentLang === "zh"
          ? "第十八届世界精武武术锦标赛暨文化大会.pdf"
          : "18th_World_Chin_Woo_Martial_Arts_Championship_&_Culture_Assembly.pdf",
      docLang: currentLang === "zh" ? "zh" : "en",
    },
    {
      icon: "fas fa-file-pdf",
      title: t("downloads.invitationTitle"),
      description: t("downloads.invitationDescription"),
      downloadUrl:
        currentLang === "zh"
          ? "/documents/第十八届世界精武武术文化大会_邀请函.pdf"
          : "/documents/Invitation_Wolrd_Chin_Woo_Convention_2026.pdf",
      fileName:
        currentLang === "zh"
          ? "第十八届世界精武武术文化大会_邀请函.pdf"
          : "Invitation_Wolrd_Chin_Woo_Convention_2026.pdf",
      docLang: currentLang === "zh" ? "zh" : "en",
    },
    {
      icon: "fas fa-file-pdf",
      title: t("sponsoringBenefits.downloadEventmappe"),
      description: t("downloads.eventmapDescription"),
      downloadUrl: "/documents/Event-Mappe.pdf",
      fileName: "GCWAA-Event-Mappe.pdf",
      docLang: "de",
    },
  ], [currentLang, t]);

  useEffect(() => {
    const fetchAllFileInfo = async () => {
      setLoading(true);
      const info = {};
      for (const item of downloadItems) {
        info[item.downloadUrl] = await fetchFileInfo(item.downloadUrl);
      }
      setFileInfo(info);
      setLoading(false);
    };
    fetchAllFileInfo();
  }, [downloadItems]);

  const handleDownload = (url, fileName) => {
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" id="downloads">
      {/* Header Section */}
      <div className="mb-8 text-center">
        <h1 className="text-4xl md:text-5xl mb-5 text-primary dark:text-primary-light relative inline-block">
          {t("downloads.title")}
          {/* Underline decoration */}
          <div className="absolute -bottom-2.5 left-1/2 transform -translate-x-1/2 w-15 h-0.5 bg-primary dark:bg-primary-light rounded-sm" />
        </h1>
        <p className="text-slate-600 dark:text-slate-400 mt-4 text-lg max-w-3xl mx-auto">
          {t("downloads.description")}
        </p>
      </div>

      {/* Download Cards Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(3)].map((_, i) => (
            <div
              key={i}
              className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden animate-pulse"
            >
              <div className="w-full h-64 bg-slate-200 dark:bg-slate-700"></div>
              <div className="p-6">
                <div className="h-5 bg-slate-200 dark:bg-slate-700 rounded w-3/4 mb-2"></div>
                <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-full mb-2"></div>
                <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-2/3 mb-4"></div>
                <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-24 mb-4"></div>
                <div className="h-12 bg-slate-200 dark:bg-slate-700 rounded-full w-full"></div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {downloadItems.map((item, index) => (
            <div
              key={index}
              className="bg-white dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden hover:shadow-md transition-shadow relative"
            >
              {/* PDF Preview */}
              <div className="relative">
                <PDFPreview url={item.downloadUrl} />
                {/* Language Flag - schräg oben rechts */}
                <div className="absolute top-0 right-0 transform rotate-12 translate-x-2 -translate-y-2">
                  <div className="relative">
                    <img
                      src={getDocLangFlag(item.docLang)}
                      alt={item.docLang === "zh" ? "中文" : item.docLang === "de" ? "Deutsch" : "English"}
                      className="w-12 h-12 object-cover rounded-full border-2 border-white dark:border-slate-800 shadow-lg"
                    />
                    <div className="absolute inset-0 rounded-full bg-gradient-to-br from-white/20 to-transparent"></div>
                  </div>
                </div>
              </div>

              {/* Card Content */}
              <div className="p-6">
                {/* Title */}
                <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
                  {item.title}
                </h3>

                {/* Description */}
                <p className="text-sm text-slate-600 dark:text-slate-400 mb-4 line-clamp-2">
                  {item.description}
                </p>

                {/* File Info with PDF Icon */}
                <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400 mb-4">
                  <i className={`${item.icon} text-slate-400 dark:text-slate-500`}></i>
                  <span>PDF</span>
                  <span className="mx-1">•</span>
                  <i className="fas fa-calendar text-slate-400 dark:text-slate-500"></i>
                  <span>{fileInfo[item.downloadUrl]?.date || "01.03.2024"}</span>
                  <span className="mx-1">•</span>
                  <i className="fas fa-file text-slate-400 dark:text-slate-500"></i>
                  <span>{fileInfo[item.downloadUrl]?.size || "0 MB"}</span>
                </div>

                {/* Download Button - moderner und runder ohne scale */}
                <button
                  onClick={() => handleDownload(item.downloadUrl, item.fileName)}
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary dark:bg-primary-light text-white text-sm font-medium rounded-full hover:bg-primary-dark dark:hover:bg-primary transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary/20 hover:shadow-lg"
                >
                  <i className="fas fa-download text-xs"></i>
                  {t("downloads.downloadButton")}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default Downloads;
